/* AiAssistantDock — collapsible right-side helper that lets the user chat
 * with Gemini Flash while working inside any AI Studio step.
 *
 * It's stateless w.r.t. the wizard: it just posts to POST /api/ai/generate
 * with an optional context prefix ("kind" describes which step is active).
 *
 * Design: MMS HUB CENTER black / white minimal.
 */
import { useState } from "react";
import { Sparkles, X, MessageSquare, Loader2, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

export function AiAssistantDock({ kind = "generic", context = "", suggestions = [] }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const ask = async (prompt) => {
    const text = prompt ?? input;
    if (!text.trim()) return;
    setMessages(m => [...m, { role: "user", content: text }]);
    setInput(""); setBusy(true);
    try {
      const r = await api.post("/ai/generate", { prompt: text, context, kind });
      setMessages(m => [...m, { role: "ai", content: r.data.content || "" }]);
    } catch (e) {
      setMessages(m => [...m, { role: "ai", content: `Errore: ${formatApiErrorDetail(e.response?.data?.detail) || e.message}` }]);
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 bg-[#0A0A0A] text-white px-4 py-3 shadow-lg inline-flex items-center gap-2 hover:bg-[#111] z-50"
        data-testid="ai-dock-open"
      >
        <Sparkles size={14}/> <span className="text-xs uppercase tracking-widest">Assistente</span>
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-6 right-6 w-[380px] max-w-[92vw] h-[520px] bg-white border border-[#0A0A0A] shadow-2xl flex flex-col z-50"
      data-testid="ai-dock"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5] bg-[#FAFAFA]">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest">
          <MessageSquare size={12}/> Assistente AI
        </div>
        <button onClick={() => setOpen(false)} className="text-[#525252] hover:text-[#0A0A0A]" data-testid="ai-dock-close">
          <X size={14}/>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3 text-sm">
        {messages.length === 0 && (
          <div className="text-[#737373] text-xs">
            <p className="mb-2">Chiedi consigli su descrizioni, materiali, costruzione, tech pack…</p>
            {suggestions.length > 0 && (
              <div className="space-y-2">
                <div className="uppercase tracking-widest text-[10px] text-[#525252]">Suggerimenti</div>
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => ask(s)} className="block w-full text-left border border-[#E5E5E5] hover:border-[#0A0A0A] p-2 text-xs">
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "text-right" : ""}>
            <div className={`inline-block max-w-[90%] whitespace-pre-wrap px-3 py-2 text-xs ${m.role === "user" ? "bg-[#0A0A0A] text-white" : "bg-[#FAFAFA] border border-[#E5E5E5] text-[#0A0A0A]"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="text-xs text-[#737373] inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin"/> Sto pensando…</div>
        )}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); if (!busy) ask(); }}
        className="border-t border-[#E5E5E5] p-2 flex items-center gap-2"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="input-dark flex-1"
          placeholder="Scrivi una domanda…"
          data-testid="ai-dock-input"
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          className="btn-gold text-xs px-3 py-2 disabled:opacity-40 inline-flex items-center gap-1"
          data-testid="ai-dock-send"
        >
          <Send size={12}/>
        </button>
      </form>
    </div>
  );
}

/** Small inline "Ask AI" button that fills the associated field via callback. */
export function AskAiButton({ prompt, context = "", kind = "generic", onResult, label = "Ask AI" }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!prompt) return;
    setBusy(true);
    try {
      const r = await api.post("/ai/generate", { prompt, context, kind });
      onResult?.(r.data.content || "");
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };
  return (
    <button
      type="button" onClick={run} disabled={busy}
      className="text-[10px] uppercase tracking-widest text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-1 disabled:opacity-40"
      data-testid="ask-ai-btn"
    >
      {busy ? <Loader2 size={10} className="animate-spin"/> : <Sparkles size={10}/>} {label}
    </button>
  );
}
